import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Avatar,
  Tooltip,
  Chip,
  keyframes,
  useTheme
} from '@mui/material';
import { Person, AutoAwesome, Article, LightbulbOutlined, Architecture, TableChart, ExpandMore, ExpandLess, CheckCircle } from '@mui/icons-material';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// eslint-disable-next-line no-unused-vars -- retained (WIP)
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

// eslint-disable-next-line no-unused-vars -- retained (WIP)
const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Confidence color helper
const confidenceColor = (c) => {
  if (c >= 0.8) return '#22c55e';
  if (c >= 0.5) return '#f59e0b';
  return '#ef4444';
};

// BOM Card Component for displaying extracted BOM in chat
function BOMCard({ bom, cadMode }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);
  const primaryColor = cadMode ? '#8b5cf6' : '#667eea';

  // Support both old format (summary, layers) and new AI-extracted format (bom_items with confidence)
  const bomItems = Array.isArray(bom?.bom_items) ? bom.bom_items : [];
  const drawingName = bom?.drawing_name || '';
  const totalItems = bom?.total_items || bomItems.length;
  const highConf = bom?.high_confidence_count || bomItems.filter(i => (i.confidence || 0) >= 0.8).length;
  const lowConf = bom?.low_confidence_count || bomItems.filter(i => (i.confidence || 0) < 0.8).length;
  const titleBlock = bom?.title_block;
  // Detect whether this is the new AI-extracted format
  const isAIExtracted = bomItems.length > 0 && bomItems[0].confidence !== undefined;

  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        bgcolor: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          bgcolor: 'rgba(139, 92, 246, 0.1)',
          borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableChart sx={{ color: primaryColor, fontSize: 18 }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: primaryColor, display: 'block' }}>
              Bill of Materials Extracted
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {drawingName}{isAIExtracted ? ' \u2022 AI-powered extraction' : ''}
            </Typography>
          </Box>
        </Box>
        {expanded ? <ExpandLess sx={{ color: 'text.secondary' }} /> : <ExpandMore sx={{ color: 'text.secondary' }} />}
      </Box>

      {/* Summary Stats - Always visible */}
      <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip
          icon={<TableChart sx={{ fontSize: 14 }} />}
          label={`${totalItems} items`}
          size="small"
          sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(139, 92, 246, 0.1)', color: primaryColor, '& .MuiChip-icon': { color: primaryColor } }}
        />
        {isAIExtracted && highConf > 0 && (
          <Chip
            label={`${highConf} high confidence`}
            size="small"
            sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
          />
        )}
        {isAIExtracted && lowConf > 0 && (
          <Chip
            label={`${lowConf} need review`}
            size="small"
            sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
          />
        )}
      </Box>

      {/* Expanded Content */}
      {expanded && (
        <Box sx={{ p: 1.5, pt: 0 }}>
          {/* Title Block */}
          {titleBlock && Object.keys(titleBlock).length > 0 && (
            <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', width: '100%', mb: 0.5 }}>
                Title Block
              </Typography>
              {Object.entries(titleBlock).map(([k, v]) => v && (
                <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              ))}
            </Box>
          )}

          {/* BOM Items Table */}
          {bomItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Parts ({bomItems.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>#</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>Part No.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>Description</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>Material</th>
                      {isAIExtracted && (
                        <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>Conf.</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {bomItems.slice(0, 15).map((item, idx) => (
                      <tr key={idx} style={{
                        backgroundColor: isAIExtracted && (item.confidence || 0) < 0.5
                          ? (isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)')
                          : 'transparent',
                      }}>
                        <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}>
                          {item.item_number || idx + 1}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                          {item.part_number || item.name || '\u2014'}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}>
                          {item.description || '\u2014'}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, textAlign: 'right', fontWeight: 600, color: primaryColor }}>
                          {item.quantity || 1}
                        </td>
                        <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, color: isDark ? '#aaa' : '#666' }}>
                          {item.material || '\u2014'}
                        </td>
                        {isAIExtracted && (
                          <td style={{ padding: '4px 8px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, textAlign: 'center' }}>
                            {item.user_edited ? (
                              <CheckCircle sx={{ color: '#22c55e', fontSize: 14 }} />
                            ) : (
                              <span style={{
                                color: confidenceColor(item.confidence || 0),
                                fontWeight: 600,
                                fontSize: '0.65rem',
                              }}>
                                {Math.round((item.confidence || 0) * 100)}%
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {bomItems.length > 15 && (
                      <tr>
                        <td colSpan={isAIExtracted ? 6 : 5} style={{ padding: '4px 8px', textAlign: 'center', color: isDark ? '#777' : '#999', fontStyle: 'italic' }}>
                          +{bomItems.length - 15} more items...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function MessageList({ messages, allFiles, setInput, suggestedQuestions, cadMode = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const userScrolledUpRef = useRef(false);

  // Colors based on mode
  const primaryColor = cadMode ? '#8b5cf6' : '#667eea';
  const gradientStart = cadMode ? '#8b5cf6' : '#667eea';
  const gradientEnd = cadMode ? '#6366f1' : '#764ba2';

  // Track if user has scrolled up — if so, don't force them back down
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // "Near bottom" = within 150px of the bottom
      userScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 150;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to bottom when messages change, but only if user hasn't scrolled up
  useEffect(() => {
    if (messagesEndRef.current && !userScrolledUpRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <Paper
      ref={containerRef}
      elevation={0}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        mb: 2,
        borderRadius: 3,
        background: isDark
          ? (cadMode
              ? 'linear-gradient(180deg, #1a1625 0%, #0f0d14 100%)'
              : 'linear-gradient(180deg, #1a1a1a 0%, #121212 100%)')
          : (cadMode
              ? 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 100%)'
              : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'),
        border: '1px solid',
        borderColor: 'divider',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
          borderRadius: '3px',
        },
      }}
    >
      {messages.length === 0 ? (
        <Box sx={{
          p: 2,
          pt: 3,
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          overflow: 'auto',
        }}>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2, maxWidth: 280, flexShrink: 0 }}>
            {cadMode
              ? 'CAD assistant ready. Ask me to create, modify, or analyze AutoCAD drawings.'
              : 'Your AI engineering assistant. Ask questions about your projects, files, and workflows.'}
          </Typography>

          {!cadMode && allFiles.length === 0 && (
            <Box sx={{
              mt: 1,
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(102, 126, 234, 0.08)',
              border: '1px dashed rgba(102, 126, 234, 0.3)',
            }}>
              <Typography variant="body2" color="text.secondary">
                Upload engineering files for better context!
              </Typography>
            </Box>
          )}

          {/* Suggested Questions */}
          <Box sx={{ mt: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
              <LightbulbOutlined sx={{ fontSize: 16, color: cadMode ? '#8b5cf6' : 'warning.main' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Try asking:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              {suggestedQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  size="small"
                  clickable
                  onClick={() => setInput(question)}
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    py: 0.5,
                    px: 1,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: primaryColor,
                      bgcolor: cadMode ? 'rgba(139, 92, 246, 0.05)' : 'rgba(102, 126, 234, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: cadMode ? '0 2px 8px rgba(139, 92, 246, 0.15)' : '0 2px 8px rgba(102, 126, 234, 0.15)',
                    },
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      fontSize: '0.8rem',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {messages.map((message, index) => {
            const isUser = message.role === 'user';
            // eslint-disable-next-line no-unused-vars -- retained (WIP)
            const isLastMessage = index === messages.length - 1;

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: isUser ? 'row-reverse' : 'row',
                  gap: 1.5,
                  animation: `${fadeInUp} 0.3s ease`,
                  animationFillMode: 'backwards',
                  animationDelay: `${index * 0.05}s`,
                }}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    background: isUser
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                      : `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                    boxShadow: isUser
                      ? '0 2px 8px rgba(59, 130, 246, 0.3)'
                      : cadMode
                        ? '0 2px 8px rgba(139, 92, 246, 0.3)'
                        : '0 2px 8px rgba(102, 126, 234, 0.3)',
                    flexShrink: 0,
                  }}
                >
                  {isUser ? <Person sx={{ fontSize: 20 }} /> : cadMode ? <Architecture sx={{ fontSize: 18 }} /> : <AutoAwesome sx={{ fontSize: 18 }} />}
                </Avatar>

                {/* Message Bubble */}
                <Box sx={{
                  maxWidth: '80%',
                  minWidth: 0,
                }}>
                  {/* Header */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mb: 0.5,
                      textAlign: isUser ? 'right' : 'left',
                      fontWeight: 500,
                    }}
                  >
                    {isUser ? 'You' : (cadMode ? 'CAD Agent' : 'e-pilot')} • {message.timestamp}
                  </Typography>

                  {/* Thinking Steps */}
                  {message.thinking && message.thinking.length > 0 && (
                    <Box sx={{
                      mb: 1,
                      p: 1.5,
                      bgcolor: cadMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(102, 126, 234, 0.08)',
                      borderRadius: 2,
                      borderLeft: `3px solid ${primaryColor}`,
                    }}>
                      {message.thinking.map((step, idx) => (
                        <Typography
                          key={idx}
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            lineHeight: 1.6,
                          }}
                        >
                          <Box component="span" sx={{ color: primaryColor, mr: 0.5 }}>●</Box>
                          {step}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Tool Calls (CAD mode) */}
                  {message.toolCalls && message.toolCalls.length > 0 && (
                    <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {message.toolCalls.map((tc, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={tc.name || tc.tool || 'tool'}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                            color: '#f57c00',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Content Bubble */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: isUser ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : 'background.paper',
                      background: isUser
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                        : undefined,
                      color: isUser ? 'white' : 'text.primary',
                      boxShadow: isUser
                        ? '0 2px 12px rgba(59, 130, 246, 0.25)'
                        : isDark ? '0 1px 4px rgba(0, 0, 0, 0.3)' : '0 1px 4px rgba(0, 0, 0, 0.08)',
                      border: isUser ? 'none' : '1px solid',
                      borderColor: 'divider',
                      borderTopRightRadius: isUser ? 4 : 20,
                      borderTopLeftRadius: isUser ? 20 : 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.6,
                        color: message.error ? 'error.main' : 'inherit',
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Box>

                  {/* Drawing Created Notification */}
                  {message.drawingCreated && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: cadMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid',
                        borderColor: cadMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(34, 197, 94, 0.3)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, color: cadMode ? '#8b5cf6' : '#22c55e', display: 'block', mb: 0.5 }}>
                        ✓ Drawing Created
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        View it in the <strong>CAD Viewer</strong> section (Tools → CAD Viewer)
                      </Typography>
                    </Box>
                  )}

                  {/* BOM Extracted Card */}
                  {message.bomExtracted && (
                    <BOMCard bom={message.bomExtracted} cadMode={cadMode} />
                  )}

                  {/* Citations / Sources */}
                  {message.citations && message.citations.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        Sources
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {message.citations.map((citation, idx) => {
                          // Support both old format (s3Location) and new format (doc_title + source_label)
                          let label = 'Unknown Source';
                          let tooltip = '';
                          if (citation.doc_title && citation.source_label) {
                            label = `${citation.doc_title} — ${citation.source_label}`;
                            tooltip = citation.ref ? `[${citation.ref}] Relevance: ${Math.round((citation.score || 0) * 100)}%` : `Relevance: ${Math.round((citation.score || 0) * 100)}%`;
                          } else if (citation.location?.s3Location?.uri) {
                            const uri = citation.location.s3Location.uri;
                            const parts = uri.split('/');
                            label = parts.length > 0 ? parts[parts.length - 1] : 'Source';
                            if (label.includes('_')) label = label.substring(label.indexOf('_') + 1);
                            if (label.endsWith('.txt')) label = label.substring(0, label.length - 4);
                            tooltip = `Relevance: ${Math.round((citation.score || 0) * 100)}%`;
                          }

                          return (
                            <Tooltip key={idx} title={tooltip} placement="top-start">
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: cadMode ? 'rgba(139, 92, 246, 0.06)' : 'rgba(102, 126, 234, 0.06)',
                                border: '1px solid',
                                borderColor: cadMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(102, 126, 234, 0.15)',
                                cursor: 'default',
                                '&:hover': {
                                  bgcolor: cadMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(102, 126, 234, 0.12)',
                                },
                                transition: 'all 0.15s ease',
                              }}>
                                <Article sx={{ fontSize: 14, color: primaryColor, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                  {citation.ref && <Box component="span" sx={{ fontWeight: 700, color: primaryColor, mr: 0.5 }}>[{citation.ref}]</Box>}
                                  {label}
                                </Typography>
                                {citation.score > 0 && (
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', ml: 'auto', flexShrink: 0 }}>
                                    {Math.round(citation.score * 100)}%
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </Box>
      )}
    </Paper>
  );
}

export default MessageList;
